// src/components/games/TicTacToeGame.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiRefreshCw, FiUser, FiCpu, FiAward } from 'react-icons/fi';

const TicTacToeGame = ({ onBack }) => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);
  const [gameMode, setGameMode] = useState(null); // 'computer' or 'friend'
  const [winner, setWinner] = useState(null);
  const [winningLine, setWinningLine] = useState([]);
  const [scores, setScores] = useState({ x: 0, o: 0, draws: 0 });

  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // Rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // Columns
      [0, 4, 8], [2, 4, 6] // Diagonals
    ];

    for (let line of lines) {
      const [a, b, c] = line;
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return { winner: squares[a], line };
      }
    }
    return null;
  };

  const isBoardFull = (squares) => {
    return squares.every(square => square !== null);
  };

  const computerMove = (currentBoard) => {
    // AI بسيط - يختار أول خانة فارغة أو يحاول الفوز/المنع
    const availableSquares = currentBoard
      .map((square, index) => square === null ? index : null)
      .filter(val => val !== null);

    if (availableSquares.length === 0) return;

    // محاولة الفوز
    for (let index of availableSquares) {
      const testBoard = [...currentBoard];
      testBoard[index] = 'O';
      if (calculateWinner(testBoard)) {
        return index;
      }
    }

    // منع اللاعب من الفوز
    for (let index of availableSquares) {
      const testBoard = [...currentBoard];
      testBoard[index] = 'X';
      if (calculateWinner(testBoard)) {
        return index;
      }
    }

    // اختر الوسط إذا كان فارغاً
    if (availableSquares.includes(4)) return 4;

    // اختر الزوايا
    const corners = [0, 2, 6, 8].filter(i => availableSquares.includes(i));
    if (corners.length > 0) {
      return corners[Math.floor(Math.random() * corners.length)];
    }

    // اختر أي خانة متاحة
    return availableSquares[Math.floor(Math.random() * availableSquares.length)];
  };

  useEffect(() => {
    const result = calculateWinner(board);
    if (result) {
      setWinner(result.winner);
      setWinningLine(result.line);
      setScores(prev => ({
        ...prev,
        [result.winner.toLowerCase()]: prev[result.winner.toLowerCase()] + 1
      }));
    } else if (isBoardFull(board) && !winner) {
      setWinner('draw');
      setScores(prev => ({ ...prev, draws: prev.draws + 1 }));
    }
  }, [board, winner]);

  useEffect(() => {
    if (gameMode === 'computer' && !isXNext && !winner) {
      const timer = setTimeout(() => {
        const move = computerMove(board);
        if (move !== undefined) {
          handleClick(move, true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isXNext, gameMode, winner, board]);

  const handleClick = (index, isComputer = false) => {
    if (board[index] || winner) return;
    if (gameMode === 'computer' && !isXNext && !isComputer) return;

    const newBoard = [...board];
    newBoard[index] = isXNext ? 'X' : 'O';
    setBoard(newBoard);
    setIsXNext(!isXNext);
  };

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setIsXNext(true);
    setWinner(null);
    setWinningLine([]);
  };

  const resetAll = () => {
    resetGame();
    setScores({ x: 0, o: 0, draws: 0 });
    setGameMode(null);
  };

  if (!gameMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-8 shadow-2xl border-2 border-blue-100"
          >
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">⭕❌</div>
              <h2 className="text-3xl font-black text-gray-900 mb-2">لعبة XO</h2>
              <p className="text-gray-600">اختر وضع اللعب</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => setGameMode('computer')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
              >
                <FiCpu size={24} />
                <span>ضد الكمبيوتر</span>
              </button>

              <button
                onClick={() => setGameMode('friend')}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
              >
                <FiUser size={24} />
                <span>ضد صديق</span>
              </button>

              <button
                onClick={onBack}
                className="w-full bg-gray-100 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <FiArrowLeft size={20} />
                <span>رجوع</span>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-md hover:shadow-lg transition text-gray-700 font-bold"
          >
            <FiArrowLeft size={18} />
            <span className="text-sm">رجوع</span>
          </button>

          <button
            onClick={resetAll}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-md hover:shadow-lg transition text-gray-700 font-bold"
          >
            <FiRefreshCw size={18} />
            <span className="text-sm">إعادة ضبط</span>
          </button>
        </div>

        {/* Scores */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-blue-200 text-center">
            <div className="text-2xl font-black text-blue-600 mb-1">{scores.x}</div>
            <div className="text-xs font-bold text-gray-600">X (أنت)</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-gray-200 text-center">
            <div className="text-2xl font-black text-gray-600 mb-1">{scores.draws}</div>
            <div className="text-xs font-bold text-gray-600">تعادل</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-pink-200 text-center">
            <div className="text-2xl font-black text-pink-600 mb-1">{scores.o}</div>
            <div className="text-xs font-bold text-gray-600">
              O ({gameMode === 'computer' ? 'كمبيوتر' : 'صديق'})
            </div>
          </div>
        </div>

        {/* Current Turn */}
        {!winner && (
          <div className="bg-white rounded-xl p-4 shadow-md text-center mb-6">
            <p className="text-sm font-bold text-gray-600 mb-1">دور</p>
            <div className="text-3xl font-black" style={{ color: isXNext ? '#2563eb' : '#db2777' }}>
              {isXNext ? 'X' : 'O'}
            </div>
          </div>
        )}

        {/* Winner Announcement */}
        <AnimatePresence>
          {winner && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20 }}
              className={`rounded-2xl p-6 shadow-xl mb-6 text-center ${
                winner === 'draw'
                  ? 'bg-gradient-to-r from-gray-100 to-gray-200'
                  : winner === 'X'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-600'
                  : 'bg-gradient-to-r from-purple-500 to-pink-600'
              }`}
            >
              <div className="text-5xl mb-3">
                {winner === 'draw' ? '🤝' : '🏆'}
              </div>
              <h3 className={`text-2xl font-black mb-2 ${winner === 'draw' ? 'text-gray-800' : 'text-white'}`}>
                {winner === 'draw' ? 'تعادل!' : `${winner} فاز!`}
              </h3>
              <button
                onClick={resetGame}
                className={`mt-4 px-6 py-3 rounded-xl font-bold shadow-lg transition-all ${
                  winner === 'draw'
                    ? 'bg-gray-800 text-white hover:bg-gray-900'
                    : 'bg-white text-gray-900 hover:bg-gray-100'
                }`}
              >
                العب مرة أخرى
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Game Board */}
      <div className="max-w-md mx-auto">
        <div className="grid grid-cols-3 gap-3 bg-white p-4 rounded-3xl shadow-2xl border-2 border-blue-100">
          {board.map((square, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: square ? 1 : 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleClick(index)}
              className={`aspect-square rounded-2xl font-black text-4xl sm:text-5xl flex items-center justify-center transition-all duration-300 ${
                winningLine.includes(index)
                  ? square === 'X'
                    ? 'bg-gradient-to-br from-blue-400 to-cyan-500 text-white shadow-xl'
                    : 'bg-gradient-to-br from-purple-400 to-pink-500 text-white shadow-xl'
                  : square
                  ? square === 'X'
                    ? 'bg-gradient-to-br from-blue-100 to-cyan-100 text-blue-600'
                    : 'bg-gradient-to-br from-purple-100 to-pink-100 text-pink-600'
                  : 'bg-gray-50 hover:bg-gray-100 border-2 border-gray-200'
              }`}
              disabled={!!square || !!winner}
            >
              {square}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TicTacToeGame;